// The assistant panel (UAT F-146, Tejesh 12 Oct 05:54: "a sidebar on the right … we'll widen the AI in a bit"): a
// shell-level pane on the right of the frame, its own `.mc` island beside V1's frame (shell/MissionShell.tsx), so it
// stays put across every hash route. It is a complementary landmark named *Assistant* that stays on the page while
// closed (hidden), so the top bar's Assistant button always controls an element that is there. Its head is the top
// bar's 64 px row (the panel's name and Close); Escape anywhere inside it closes it; its left edge is the resize
// handle (320–560 px). Its body is a placeholder card in his words until the panel's content is built: no vendor,
// model or product is named (DC-33).

import type { KeyboardEvent, Ref } from "react";
import { Card, IconButton, Paragraph } from "@/mc/core";
import { ASSISTANT_LABELS } from "@/mc/g4a";
import { OVERLAY_LABELS } from "@/mc/g4b";
import { cx } from "@/mc/lib";
import { ASIDE } from "./layout";
import { ResizeHandle } from "./ResizeHandle";

/** The panel's id: the top bar's Assistant button and the panel's resize handle point at it. */
export const ASSISTANT_ID = "assistant";

export interface AssistantPanelProps {
  open: boolean;
  /** Docked beside the detail pane, or a sheet over it (a phone, a narrow window). */
  sheet: boolean;
  /** The drawn width, and the most a drag may give it here. */
  width: number;
  max: number;
  /** The handle is drawn (not on a phone, where the sheet takes the width). */
  resizable: boolean;
  panelRef?: Ref<HTMLElement> | undefined;
  /** The panel's name: focus moves to it when the person opens the panel. */
  headingRef?: Ref<HTMLHeadingElement> | undefined;
  onClose: () => void;
  onDrag: (clientX: number) => void;
  onKey: (key: string) => boolean;
  onDragChange?: ((dragging: boolean) => void) | undefined;
  inert?: boolean | undefined;
}

export function AssistantPanel({
  open, sheet, width, max, resizable, panelRef, headingRef, onClose, onDrag, onKey, onDragChange, inert,
}: AssistantPanelProps) {
  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== "Escape" || event.defaultPrevented) return;
    event.preventDefault();
    onClose();
  };
  return (
    <aside
      ref={panelRef}
      id={ASSISTANT_ID}
      className={cx("mc", "mc-asst", sheet && "mc-asst-sheet")}
      role="complementary"
      aria-label={ASSISTANT_LABELS.assistant}
      hidden={!open}
      onKeyDown={onKeyDown}
      {...(inert ? { inert: "" } : {})}
    >
      {resizable ? (
        <ResizeHandle
          label={ASSISTANT_LABELS.assistant}
          controls={ASSISTANT_ID}
          value={width}
          min={ASIDE.min}
          max={max}
          edge="start"
          onDrag={onDrag}
          onKey={onKey}
          onDragChange={onDragChange}
          
        />
      ) : null}
      <div className="mc-asst-h">
        <h2 ref={headingRef} className="mc-asst-t" tabIndex={-1}>{ASSISTANT_LABELS.assistant}</h2>
        <IconButton icon="x" label={OVERLAY_LABELS.close} onClick={onClose} aria-controls={ASSISTANT_ID}  />
      </div>
      <div className="mc-asst-b">
        <Card title={ASSISTANT_LABELS.panel} tight>
          <Paragraph>{ASSISTANT_LABELS.humansDecide}</Paragraph>
        </Card>
      </div>
    </aside>
  );
}
